
import { defineComponent, ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import Loader from '@/components/Loader.vue'
import { useLanguage } from '@/lang/language'
import { AuthService } from '@/services/authService'

interface MetaData {
  issuer: string
  email: string
  loginHint: string
  acr_values: string
}

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export default defineComponent({
  components: { Loader },
  props: {
    acr_values: {
      type: String,
      default: ''
    },
    tenantId: {
      type: String,
      default: ''
    },
    companyCode: {
      type: String,
      default: ''
    },
    startpage: {
      type: String,
      default: ''
    },
    fallback: {
      type: String,
      default: ''
    },
    meetingsId: {
      type: String,
      default: ''
    },
    meetingsActionId: {
      type: String,
      default: ''
    },
    loginHint: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    const email = ref<string>()
    const isInitializing = ref<boolean>(true)
    const isHandlingSubmit = ref<boolean>(false)
    const error = ref<string>()
    const input = ref()

    const { replace } = useRouter()
    const { language } = useLanguage()

    const isValidEmail = computed(() => {
      const temp = email.value

      return !!temp && emailRegex.test(temp)
    })

    onMounted(async () => {
      const errorString = localStorage.getItem('error')
      localStorage.removeItem('metadata')
      if (errorString) {
        const error = JSON.parse(errorString)
        localStorage.removeItem('error')
        history.replaceState({}, '', location.origin)
        await replace({
          name: 'error',
          params: { message: error.message }
        })
        return
      }
      if (props.companyCode) {
        localStorage.setItem('companyCode', props.companyCode)
      }
      if (props.startpage) {
        localStorage.setItem('startpage', props.startpage)
      }
      if (props.fallback) {
        localStorage.setItem('fallback', props.fallback)
      }
      if (props.meetingsId) {
        localStorage.setItem('meetingsId', props.meetingsId)
      }
      if (props.meetingsActionId) {
        localStorage.setItem('meetingsActionId', props.meetingsActionId)
      }
      if (props.tenantId) {
        await handleSubmit()
      }

      const metadata = localStorage.getItem('metadata')

      if (metadata) {
        await initAuth(JSON.parse(metadata))
      } else {
        isInitializing.value = false
      }

      input.value?.focus()
    })

    const getSwarmDomain = async () => {
      const result = await fetch('appsettings.json')
      const { swarm_domain } = await result.json()

      return swarm_domain
    }

    const handleSubmit = async () => {
      isHandlingSubmit.value = true
      error.value = ''

      try {
        const issuer = await getIssuer(props.tenantId)
        const metadata: MetaData = {
          issuer: issuer,
          email: email.value as string,
          loginHint: props.loginHint as string,
          acr_values: props.acr_values as string
        }

        localStorage.setItem('tempMetadata', JSON.stringify(metadata))
        await initAuth(metadata)
      } catch (e) {
        error.value = language.value.no_organisation_found
        isHandlingSubmit.value = false
      }
    }

    const getIssuer = async (tenantId: string | undefined) => {
      const swarm_domain = await getSwarmDomain()
      if (tenantId) {
        return getIssuerByTenantId(tenantId, swarm_domain)
      } else {
        return getIssuerByAliasOrEmail(swarm_domain)
      }
    }

    const getIssuerByTenantId = async (tenantId: string, swarm_domain: string) => {
      const result = await fetch(
        `https://login.${swarm_domain}/${tenantId}/.well-known/openid-configuration`
      )
      const { issuer } = await result.json()
      return issuer
    }

    const getIssuerByAliasOrEmail = async (swarm_domain: string) => {
      let data = { AliasOrEmail: email.value }

      const result = await fetch(`https://login.${swarm_domain}/common/tenantinfo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
      const { authority } = await result.json()
      return authority
    }

    const initAuth = async (metadata: MetaData) => {
      const authService = new AuthService(metadata.issuer)
      authService.Ready.then(async () => {
        const login_hint = metadata.loginHint ? metadata.loginHint : metadata.email
        await authService.login(login_hint, metadata.acr_values)
      })
    }

    return {
      email,
      isInitializing,
      isHandlingSubmit,
      error,
      language,
      handleSubmit,
      isValidEmail
    }
  }
})
